import type { BigStepKey } from "../../schadenmeldung-big/types";

export interface Beteiligter {
  rolle: string;
  name: string;
  vorname: string;
  tel: string;
  email: string;
  schritt: BigStepKey;
}

type Rec = Record<string, unknown>;

const rec = (x: unknown): Rec => (x && typeof x === "object" && !Array.isArray(x) ? (x as Rec) : {});
const str = (x: unknown): string => (typeof x === "string" ? x.trim() : "");
const liste = (x: unknown): unknown[] => (Array.isArray(x) ? x : []);

/** Leitet die Beteiligten (Rolle, Kontaktdaten, Meldungsschritt) aus den Meldungsantworten ab. */
export function beteiligteAusMeldung(meldung: Rec | null | undefined): Beteiligter[] {
  const m = rec(meldung);
  const out: Beteiligter[] = [];

  const push = (rolle: string, contact: unknown, schritt: BigStepKey) => {
    const c = rec(contact);
    const name = str(c.name);
    const vorname = str(c.vorname);
    if (!name && !vorname) return;
    out.push({ rolle, name, vorname, tel: str(c.tel) || str(c.handy), email: str(c.email), schritt });
  };

  const art = m.damageType;
  // Melder und Ansprechpartner gibt es in jeder Schadenart. Fehlt die Art noch
  // (Entwurf), bleiben sie trotzdem sichtbar; der Sprung führt dann auf den
  // Schritt „Schadenart", wo die Art gewählt wird.
  const melderSchritt: BigStepKey =
    art === "gebaeude" ? "geb_melder" : art === "kfz" ? "kfz_melder" : art === "sonstiges" ? "son_melder" : "type";

  push(
    "Melder",
    { vorname: m.melderVorname, name: m.melderName, tel: m.melderTel, handy: m.melderHandy, email: m.melderEmail },
    melderSchritt,
  );
  if (m.apWer === "andere") push("Ansprechpartner", m.apContact, melderSchritt);

  if (art === "gebaeude") {
    push("Mieter", m.gebMieterContact, "geb_where");
    push("Verursacher", m.gebVerursacherContact, "geb_verursacher");
    push("Weitere Betroffene", m.gebWeitereContact, "geb_verursacher");
  } else if (art === "kfz") {
    push("Fahrer", m.kfzEigenFahrer, "kfz_eigenes");
    liste(m.kfzFremdFahrzeuge).forEach((f, i) => {
      push(`Fahrer (Fremdfahrzeug ${i + 1})`, rec(f).fahrer, "kfz_fremdes");
      push(`Halter (Fremdfahrzeug ${i + 1})`, rec(f).halter, "kfz_fremdes");
    });
    liste(m.kfzPersonen).forEach((p) => push("Verletzte", { name: rec(p).name }, "kfz_person"));
    push("Zeuge", m.kfzZeugenContact, "kfz_hergang");
  } else {
    push("Eigentümer der Sache", m.sonSacheFremdContact, "son_details");
    push("Betroffene Person", m.sonMenschContact, "son_details");
    liste(m.sonMenschWeitere).forEach((p) => push("Weitere Verletzte", rec(p).contact, "son_details"));
    push("Betroffener", m.sonSonstBetroffenContact, "son_details");
    push("Zeuge", m.sonSacheZeugenContact, "son_details");
    push("Zeuge", m.sonMenschZeugenContact, "son_details");
    push("Zeuge", m.sonSonstZeugenContact, "son_details");
  }
  return out;
}
